import { Fragment, useMemo, type FC } from 'react';

import { ThrowError } from '#apps/client-web/app/components/throw-error';
import { routes } from '#apps/client-web/app/routes';
import { LightSourceLabel } from '#apps/client-web/features/audit-form-lighting-type/constants';

import {
  type AuditLightingTypeFormEntry,
  type LightSource,
} from '#packages/data-collection/models/audit-lighting-type-form';
import { type ViewModelProps } from '#packages/web/types/view-model-props';
import { type LoggingContext } from '#packages/data-collection/types/logging-context';
import { EmptyPage, LoadingPage } from '#packages/web/layouts/page/templates';
import { Fab } from '#packages/web/foundations/buttons/fab';

import { type AuditFormLightingTypePageViewModel } from './audit-form-lighting-type-page.view-model';
import { AuditFormLightingTypePageRenderer } from './audit-form-lighting-type-page.renderer';

export type AuditFormLightingTypePageContainerProps =
  ViewModelProps<AuditFormLightingTypePageViewModel> & LoggingContext;

export const AuditFormLightingTypePageContainer: FC<
  AuditFormLightingTypePageContainerProps
> = ({ viewModel }) => {
  const { auditId, auditFormQuery, thumbnailFileIds, thumbnailMap, onCreate } =
    viewModel;

  const groups = useMemo(() => {
    const entries = auditFormQuery.data?.entries ?? [];

    // Group by lightSource; entries without a source go into 'unspecified'
    const grouped: Record<
      string,
      { entry: AuditLightingTypeFormEntry; globalIndex: number }[]
    > = {};
    entries.forEach((entry, globalIndex) => {
      const key = entry.lightSource ?? 'unspecified';
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push({ entry, globalIndex });
    });

    return Object.entries(grouped).map(([lightSource, groupEntries]) => ({
      id: lightSource,
      label:
        lightSource === 'unspecified'
          ? 'Unspecified'
          : (LightSourceLabel[lightSource as LightSource] ?? lightSource),
      entries: groupEntries.map(({ entry, globalIndex }) => ({
        link: routes.audit.getEnergyAuditLightingTypeFormEntryPath(
          auditId!,
          globalIndex,
        ),
        entry,
        thumbnail: (() => {
          const fileId = thumbnailFileIds[globalIndex];
          return fileId ? (thumbnailMap[fileId] ?? null) : null;
        })(),
      })),
    }));
  }, [auditFormQuery.data, auditId, thumbnailFileIds, thumbnailMap]);

  const isLoading = auditFormQuery.isLoading;
  const isEmpty = auditFormQuery.isSuccess && groups.length < 1;
  const hasData = !isLoading && !isEmpty;

  return (
    <Fragment>
      {isLoading && <LoadingPage />}
      {isEmpty && <EmptyPage onActionButtonClick={onCreate} />}
      {hasData && <AuditFormLightingTypePageRenderer groups={groups} />}
      {hasData && <Fab onClick={onCreate} />}
      {auditFormQuery.isError && <ThrowError error={auditFormQuery.error} />}
    </Fragment>
  );
};
